import { useEffect, useId, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { invalidateAfterCodeMutation } from 'core/api/invalidation';
import { queryKeys } from 'core/api/queryKeys';
import { ApiError } from 'core/errors/ApiError';
import { humanizeApiError } from 'core/errors/mapFieldErrors';
import { useI18n } from 'core/i18n/I18nProvider';
import { companyApi } from 'features/company/company.api';
import { useCompany } from 'features/company/CompanyContext';
import { buildCodesCsv, downloadBlob } from 'features/company/companyUtils';
import { CopyButton } from 'shared/components/CopyButton';
import { Modal } from 'shared/components/Modal';
import { TextInput } from 'shared/components/Input';
import { Button } from 'shared/components/Button';

export function CodeGenerationDialog({ open, onClose }) {
  const { t } = useI18n();
  const { companyId } = useCompany();
  const queryClient = useQueryClient();
  const [entitlementId, setEntitlementId] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [label, setLabel] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [generated, setGenerated] = useState(null);
  const quantityId = useId();
  const labelId = useId();

  const entitlementsQuery = useQuery({
    queryKey: queryKeys.companyEntitlements,
    queryFn: () => companyApi.entitlements(companyId, { page: 1, per_page: 50, status: 'active' }),
    enabled: open,
  });

  const entitlements = useMemo(() => entitlementsQuery.data?.items || [], [entitlementsQuery.data]);
  const selected = entitlements.find((item) => String(item.id) === String(entitlementId));
  const previewCredits = selected?.available_credits;

  useEffect(() => {
    if (!open) {
      setGenerated(null);
      setError('');
      setQuantity('1');
      setLabel('');
      setEntitlementId('');
    }
  }, [open]);

  useEffect(() => {
    if (open && entitlements.length > 0 && !entitlementId) {
      setEntitlementId(String(entitlements[0].id));
    }
  }, [open, entitlements, entitlementId]);

  const handleGenerate = async (event) => {
    event.preventDefault();
    if (submitting) {
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const response = await companyApi.generateCodes(companyId, {
        entitlement_id: Number(entitlementId),
        quantity: Number(quantity),
        label: label || undefined,
      });
      setGenerated(response);
      await invalidateAfterCodeMutation(queryClient);
    } catch (err) {
      if (err instanceof ApiError && err.isConflict()) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.companyEntitlements });
        await queryClient.invalidateQueries({ queryKey: queryKeys.companyCredits });
      }
      setError(humanizeApiError(err, t));
    } finally {
      setSubmitting(false);
    }
  };

  const codes = generated?.codes || [];
  const batchId = generated?.batch?.id;

  const handleDownload = () => {
    const csv = buildCodesCsv(codes);
    downloadBlob(new Blob([csv], { type: 'text/csv;charset=utf-8' }), batchId ? `pacilo-codes-batch-${batchId}.csv` : 'pacilo-codes.csv');
  };

  return (
    <Modal open={open} onClose={onClose} title={generated ? t('company.codes.generatedTitle') : t('company.codes.generateTitle')} size="wide">
      {generated ? (
        <div className="generated-codes">
          <p className="warning-banner" role="note">{t('company.codes.securityWarning')}</p>
          <ul className="code-list">
            {codes.map((entry) => {
              const code = typeof entry === 'string' ? entry : entry.code;
              return (
                <li key={code}>
                  <code dir="ltr">{code}</code>
                  <CopyButton value={code} />
                </li>
              );
            })}
          </ul>
          <div className="action-row">
            <CopyButton
              value={codes.map((entry) => (typeof entry === 'string' ? entry : entry.code)).join('\n')}
              label={t('company.codes.copyAll')}
            />
            <button type="button" className="btn btn-secondary" onClick={handleDownload}>
              {t('company.codes.downloadCsv')}
            </button>
            <button type="button" className="btn btn-primary" onClick={onClose}>{t('actions.close')}</button>
          </div>
        </div>
      ) : (
        <form onSubmit={(event) => { void handleGenerate(event); }}>
          <label className="field">
            <span>{t('company.codes.entitlement')}</span>
            <select className="input" value={entitlementId} onChange={(event) => setEntitlementId(event.target.value)} required>
              {entitlements.map((entitlement) => (
                <option key={entitlement.id} value={entitlement.id}>
                  {entitlement.plan_name || entitlement.id}
                </option>
              ))}
            </select>
          </label>
          {previewCredits != null ? (
            <p className="muted">{t('company.codes.remainingCredits', { count: previewCredits })}</p>
          ) : null}
          <TextInput
            id={quantityId}
            label={t('company.codes.quantity')}
            type="number"
            min="1"
            value={quantity}
            onChange={(event) => setQuantity(event.target.value)}
            required
          />
          <TextInput
            id={labelId}
            label={t('company.codes.batchLabel')}
            value={label}
            onChange={(event) => setLabel(event.target.value)}
          />
          {error ? <p className="field-error" role="alert">{error}</p> : null}
          <div className="modal-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose}>{t('actions.cancel')}</button>
            <Button type="submit" loading={submitting}>{t('company.codes.generate')}</Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
